import * as React from "react";

import {
  useGetContractFamilyPipelineConfigurationOptionsQuery,
  useGetContractFamilyPipelineConfigurationQuery,
  useUpdateContractFamilyPipelineConfigurationMutation,
} from "@/api/contract-family-pipeline";
import { useGetDirectoryUsersQuery } from "@/api/users-admin";
import { Button } from "@/components/ui/button";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { PipelineExecutionMode } from "@/types/contract-family-pipeline";
import { getPipelineErrorMessage } from "@/views/contract-families/pipeline/pipelineErrors";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CREATE_NEW_COLLECTION_KEY = "create-new-collection";
const CREATE_NEW_BASE_COLLECTION_KEY = "create-new-base-collection";

export const ContractFamilyConfigurationModal: React.FC<Props> = ({ open, onOpenChange }) => {
  const { formatDateTime } = useDateFormatter();
  const configuration = useGetContractFamilyPipelineConfigurationQuery(undefined, { skip: !open });
  const [templateId, setTemplateId] = React.useState("");
  const [collectionId, setCollectionId] = React.useState("");
  const [newCollectionName, setNewCollectionName] = React.useState("");
  const [baseCollectionId, setBaseCollectionId] = React.useState("");
  const [newBaseCollectionName, setNewBaseCollectionName] = React.useState("");
  const [publisherUserId, setPublisherUserId] = React.useState("");
  const [autoExecutionMode, setAutoExecutionMode] = React.useState<PipelineExecutionMode>("batch");
  const [error, setError] = React.useState<string | null>(null);
  const options = useGetContractFamilyPipelineConfigurationOptionsQuery(templateId || undefined, { skip: !open });
  const publisherUsers = useGetDirectoryUsersQuery(options.data?.publisher_user_ids ?? [], {
    skip: !open || !options.data?.publisher_user_ids.length,
  });
  const [updateConfiguration, updateState] = useUpdateContractFamilyPipelineConfigurationMutation();

  React.useEffect(() => {
    if (!open) return;
    setError(null);
    setTemplateId(configuration.data?.fixed_template_id ?? "");
    setCollectionId(configuration.data?.default_family_collection_id ?? "");
    setNewCollectionName("");
    setBaseCollectionId(configuration.data?.default_base_collection_id ?? "");
    setNewBaseCollectionName("");
    setPublisherUserId(configuration.data?.publisher_user_id ?? "");
    setAutoExecutionMode(configuration.data?.auto_execution_mode ?? "batch");
  }, [open, configuration.data]);

  React.useEffect(() => {
    if (!collectionId || !options.data) return;
    if (collectionId === CREATE_NEW_COLLECTION_KEY) {
      if (!options.data.publisher_user_ids.includes(publisherUserId)) {
        setPublisherUserId("");
      }
      return;
    }
    const selectedCollection = options.data.family_collections.find(item => item.collection_id === collectionId);
    if (!selectedCollection) {
      setCollectionId("");
      setPublisherUserId("");
      return;
    }
    if (!selectedCollection.owner_user_ids.includes(publisherUserId)) {
      setPublisherUserId(selectedCollection.owner_user_ids[0] ?? "");
    }
  }, [collectionId, options.data, publisherUserId]);

  React.useEffect(() => {
    if (!baseCollectionId || baseCollectionId === CREATE_NEW_BASE_COLLECTION_KEY || !options.data) return;
    if (!options.data.base_collections.some(item => item.collection_id === baseCollectionId)) {
      setBaseCollectionId("");
    }
  }, [baseCollectionId, options.data]);

  const selectedCollection = options.data?.family_collections.find(item => item.collection_id === collectionId);
  const isCreatingCollection = collectionId === CREATE_NEW_COLLECTION_KEY;
  const isCreatingBaseCollection = baseCollectionId === CREATE_NEW_BASE_COLLECTION_KEY;
  const validBaseCollection = isCreatingBaseCollection
    ? Boolean(newBaseCollectionName.trim())
    : Boolean(baseCollectionId);
  const validSelection = Boolean(
    templateId &&
    validBaseCollection &&
    publisherUserId &&
    (isCreatingCollection
      ? newCollectionName.trim() && options.data?.publisher_user_ids.includes(publisherUserId)
      : selectedCollection?.owner_user_ids.includes(publisherUserId)),
  );

  const submit = async () => {
    if (!validSelection) return;
    setError(null);
    try {
      await updateConfiguration({
        fixed_template_id: templateId,
        publisher_user_id: publisherUserId,
        auto_execution_mode: autoExecutionMode,
        ...(isCreatingBaseCollection
          ? { default_base_collection_name: newBaseCollectionName.trim() }
          : { default_base_collection_id: baseCollectionId }),
        ...(isCreatingCollection
          ? { default_family_collection_name: newCollectionName.trim() }
          : { default_family_collection_id: collectionId }),
      }).unwrap();
      onOpenChange(false);
    } catch (requestError) {
      setError(getPipelineErrorMessage(requestError));
    }
  };

  const templateOptions = options.data?.templates ?? [];
  const collectionOptions = options.data?.family_collections ?? [];
  const baseCollectionOptions = options.data?.base_collections ?? [];
  const eligiblePublisherIds = isCreatingCollection
    ? (options.data?.publisher_user_ids ?? [])
    : (selectedCollection?.owner_user_ids ?? []);
  const directoryUsers = (publisherUsers.data ?? []).filter(user => eligiblePublisherIds.includes(user.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Contract Family configuration</DialogTitle>
          <DialogDescription>
            Select the Snapshot Template, compatible Master Collection, and controlled publisher used by the Contract
            Family flow for this organization.
          </DialogDescription>
        </DialogHeader>

        {configuration.isError || options.isError ? (
          <div role="alert" className="space-y-2 text-sm text-red-700">
            <p>{getPipelineErrorMessage(configuration.error ?? options.error)}</p>
            <Button type="button" variant="outline" size="sm" onClick={() => void configuration.refetch()}>
              Retry configuration
            </Button>
          </div>
        ) : null}

        <div className="grid gap-4">
          <label className="grid gap-1 text-sm">
            <span className="font-medium text-slate-700">Snapshot Template</span>
            <select
              aria-label="Snapshot Template"
              value={templateId}
              onChange={event => {
                setTemplateId(event.target.value);
                setCollectionId("");
                setNewCollectionName("");
                setBaseCollectionId("");
                setNewBaseCollectionName("");
                setPublisherUserId("");
              }}
              disabled={options.isFetching}
              className="rounded-md border bg-white px-3 py-2"
            >
              <option value="">Select a template</option>
              {templateOptions.map(template => (
                <option key={template.template_id} value={template.template_id}>
                  {template.name}
                </option>
              ))}
            </select>
          </label>

          <label className="grid gap-1 text-sm">
            <span className="font-medium text-slate-700">Supplier Base Collection</span>
            <select
              aria-label="Supplier Base Collection"
              value={baseCollectionId}
              onChange={event => {
                setBaseCollectionId(event.target.value);
                setNewBaseCollectionName("");
              }}
              disabled={!templateId || options.isFetching}
              className="rounded-md border bg-white px-3 py-2"
            >
              <option value="">Select a collection</option>
              <option value={CREATE_NEW_BASE_COLLECTION_KEY}>Create new Supplier Base Collection…</option>
              {baseCollectionOptions.map(collection => (
                <option key={collection.collection_id} value={collection.collection_id}>
                  {collection.name}
                </option>
              ))}
            </select>
            {isCreatingBaseCollection ? (
              <input
                aria-label="New Supplier Base Collection name"
                value={newBaseCollectionName}
                onChange={event => setNewBaseCollectionName(event.target.value)}
                maxLength={255}
                placeholder="Supplier Base Collection name"
                className="rounded-md border bg-white px-3 py-2"
              />
            ) : null}
            <span className="text-xs text-slate-500">
              Folder contract summaries snapshot new documents into this collection.
            </span>
          </label>

          <label className="grid gap-1 text-sm">
            <span className="font-medium text-slate-700">Master Collection</span>
            <select
              aria-label="Master Collection"
              value={collectionId}
              onChange={event => {
                setCollectionId(event.target.value);
                setNewCollectionName("");
                setPublisherUserId("");
              }}
              disabled={!templateId || options.isFetching}
              className="rounded-md border bg-white px-3 py-2"
            >
              <option value="">Select a compatible collection</option>
              <option value={CREATE_NEW_COLLECTION_KEY}>Create new Master Collection…</option>
              {collectionOptions.map(collection => (
                <option key={collection.collection_id} value={collection.collection_id}>
                  {collection.name}
                </option>
              ))}
            </select>
            {isCreatingCollection ? (
              <input
                aria-label="New Master Collection name"
                value={newCollectionName}
                onChange={event => setNewCollectionName(event.target.value)}
                maxLength={255}
                placeholder="Master Collection name"
                className="rounded-md border bg-white px-3 py-2"
              />
            ) : null}
            {templateId && !options.isFetching && collectionOptions.length === 0 ? (
              <span className="text-xs text-amber-700">
                No compatible existing collection is available. You can create a new one.
              </span>
            ) : null}
          </label>

          <label className="grid gap-1 text-sm">
            <span className="font-medium text-slate-700">Publisher</span>
            <select
              aria-label="Contract Family publisher"
              value={publisherUserId}
              onChange={event => setPublisherUserId(event.target.value)}
              disabled={(!selectedCollection && !isCreatingCollection) || options.isFetching}
              className="rounded-md border bg-white px-3 py-2"
            >
              <option value="">Select an owner</option>
              {(directoryUsers.length
                ? directoryUsers
                : eligiblePublisherIds.map(id => ({ id, name: id, surname: "", email: "" }))
              ).map(user => (
                <option key={user.id} value={user.id}>
                  {[user.name, user.surname].filter(Boolean).join(" ") || user.email || user.id}
                </option>
              ))}
            </select>
            <span className="text-xs text-slate-500">
              The publisher must remain an active owner in the selected collection.
            </span>
          </label>

          <label className="grid gap-1 text-sm">
            <span className="font-medium text-slate-700">Execution mode for one-click summaries</span>
            <select
              aria-label="Execution mode for one-click summaries"
              value={autoExecutionMode}
              onChange={event => setAutoExecutionMode(event.target.value === "sync" ? "sync" : "batch")}
              className="rounded-md border bg-white px-3 py-2"
            >
              <option value="batch">Batch API</option>
              <option value="sync">Synchronous</option>
            </select>
            <span className="text-xs text-slate-500">
              Batch API runs at lower provider cost and can take up to a day; synchronous runs finish sooner.
            </span>
          </label>
        </div>

        {configuration.data?.updated_by_user_name ? (
          <p className="text-xs text-slate-500">
            Last updated by <span className="font-medium">{configuration.data.updated_by_user_name}</span>
            {configuration.data.updated_at ? (
              <time dateTime={configuration.data.updated_at}> ({formatDateTime(configuration.data.updated_at)})</time>
            ) : null}
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={!validSelection || updateState.isLoading}>
            {updateState.isLoading ? "Saving…" : "Save configuration"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
